/**
 * CR460: Preview Matches panel — shows which FLYER sources an adapter would match.
 * Reads from v_image_capture_adapter_matches for a given adapter_key.
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RefreshCw, ExternalLink, Search, Eye } from "lucide-react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";

interface MatchRow {
  merchant_source_id: string | null;
  merchant_id: string | null;
  merchant_name: string | null;
  source_url: string | null;
  score: number;
  reasons: string[] | null;
  current_adapter_key: string | null;
  adapter_enabled: boolean;
}

interface Props {
  adapterKey: string;
  onAssign?: (sourceId: string) => void;
}

export function PreviewMatchesPanel({ adapterKey, onAssign }: Props) {
  const [matches, setMatches] = useState<MatchRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const fetchMatches = async () => {
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from("v_image_capture_adapter_matches")
      .select("merchant_source_id, merchant_id, merchant_name, source_url, score, reasons, current_adapter_key, adapter_enabled")
      .eq("adapter_key", adapterKey)
      .order("score", { ascending: false })
      .limit(50);
    if (!error && data) {
      setMatches(data as MatchRow[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapterKey]);

  const filtered = matches.filter((m) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (m.merchant_name ?? "").toLowerCase().includes(q) ||
      (m.merchant_id ?? "").toLowerCase().includes(q) ||
      (m.source_url ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-3">
      <Separator />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Eye className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Preview Matches</span>
          {!loading && (
            <Badge variant="secondary" className="text-xs">{matches.length}</Badge>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={fetchMatches}
          disabled={loading}
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Sources whose URLs / merchant IDs match this adapter's rules (score &gt; 0, top 50).
      </p>

      {matches.length > 3 && (
        <div className="relative">
          <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Filter…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-7 text-xs pl-7"
          />
        </div>
      )}

      {loading ? (
        <div className="text-xs text-center text-muted-foreground py-4">Loading matches…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-md border border-dashed px-4 py-6 text-center">
          <p className="text-xs text-muted-foreground">
            {matches.length === 0
              ? "No sources match this adapter's rules. Add rules above to see suggestions."
              : "No sources match your filter."}
          </p>
        </div>
      ) : (
        <div className="rounded-md border text-sm overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Merchant</TableHead>
                <TableHead className="text-xs">Source URL</TableHead>
                <TableHead className="text-xs w-[70px]">Score</TableHead>
                <TableHead className="text-xs">Reasons</TableHead>
                <TableHead className="text-xs w-[90px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((m, i) => {
                const alreadyAssigned = m.current_adapter_key === adapterKey;
                return (
                  <TableRow key={m.merchant_source_id ?? i} className={alreadyAssigned ? "bg-muted/30" : ""}>
                    <TableCell className="py-2">
                      <div className="text-xs font-medium">{m.merchant_name || m.merchant_id}</div>
                      <div className="text-[10px] text-muted-foreground">{m.merchant_id}</div>
                    </TableCell>
                    <TableCell className="py-2 max-w-[200px]">
                      <span
                        className="text-[11px] text-muted-foreground truncate block max-w-[190px]"
                        title={m.source_url ?? ""}
                      >
                        {m.source_url || "—"}
                      </span>
                    </TableCell>
                    <TableCell className="py-2">
                      <Badge variant="outline" className="text-[10px] font-mono">{m.score}</Badge>
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex flex-wrap gap-1">
                        {(m.reasons ?? []).map((r) => (
                          <Badge key={r} variant="secondary" className="text-[9px] px-1.5">
                            {r}
                          </Badge>
                        ))}
                        {alreadyAssigned && (
                          <Badge variant="default" className="text-[9px] px-1.5">assigned</Badge>
                        )}
                        {!m.adapter_enabled && (
                          <Badge variant="destructive" className="text-[9px] px-1.5">disabled</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex items-center gap-1">
                        {m.merchant_source_id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            title="Open source editor"
                            asChild
                          >
                            <a
                              href={`/admin/merchants?sourceId=${m.merchant_source_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </Button>
                        )}
                        {onAssign && m.merchant_source_id && !alreadyAssigned && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 text-[10px] px-1.5"
                            onClick={() => onAssign(m.merchant_source_id!)}
                            disabled={!m.adapter_enabled}
                            title={!m.adapter_enabled ? "Adapter is disabled — enable it first" : "Assign this adapter to source"}
                          >
                            Assign
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
